// Editor de encuestas: preguntas (tipos, obligatoria, lógica condicional, mapeo al contacto)
// y configuración (intro, mensaje final, etiquetas, listas, consentimiento).
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowLeft, ArrowUp, Copy, GitBranch, Plus, Save, Trash2 } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { Button, Card, ErrorState, Field, Input, Loading, Select, Textarea } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { CheckList, ChipsInput, DEFAULT_CONSENT_TEXT, DEFAULT_POLICY_URL, Tabs, Toggle, newId, useMktOptions } from "../forms/shared.tsx";
import { SurveyPreview } from "./SurveyPreview.tsx";
import { MAP_LABEL, QTYPE_LABEL, conditionValues, hasOptions, type ContactMap, type Question, type QuestionType, type Survey, type SurveySettings } from "./types.ts";

type Tab = "preguntas" | "configuracion";

export function SurveyEditor(): JSX.Element {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const admin = Boolean(useMe().data?.admin);
  const opts = useMktOptions();
  const q = useQuery<{ survey: Survey }>({ queryKey: ["mkt-survey", id], queryFn: () => api.get(`/mkt/surveys/${id}`) });
  const [tab, setTab] = useState<Tab>("preguntas");
  const [name, setName] = useState("");
  const [folder, setFolder] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [settings, setSettings] = useState<SurveySettings>({});
  const [dirty, setDirty] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const s = q.data?.survey;
    if (!s) return;
    setName(s.name); setFolder(s.folder ?? ""); setQuestions(s.questions ?? []); setSettings(s.settings ?? {}); setDirty(false);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => api.patch<{ survey: Survey }>(`/mkt/surveys/${id}`, { name: name.trim(), folder: folder.trim() || null, questions: questions.map(cleanQuestion), settings }),
    onSuccess: (r) => { qc.setQueryData(["mkt-survey", id], (old: unknown) => ({ ...(old as object), survey: r.survey })); void qc.invalidateQueries({ queryKey: ["mkt-surveys"] }); setDirty(false); toast.success("Encuesta guardada."); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
  });

  const setQs = (qs: Question[]): void => { setQuestions(qs); setDirty(true); };
  const setSt = (s: SurveySettings): void => { setSettings(s); setDirty(true); };

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState message="No se pudo cargar la encuesta." onRetry={() => void q.refetch()} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <button type="button" className="btn-ghost btn-sm" onClick={() => nav(`../${id}`)} aria-label="Volver"><ArrowLeft size={16} /></button>
          <h2 className="text-lg font-semibold text-ink truncate">{name || "Encuesta"}</h2>
          {dirty && <span className="text-xs text-warning">Cambios sin guardar</span>}
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { if (!dirty || window.confirm("Hay cambios sin guardar. ¿Salir de todos modos?")) nav(`../${id}`); }}>Cerrar</Button>
          {admin && <Button variant="primary" disabled={!dirty || save.isPending || !name.trim()} onClick={() => save.mutate()}><Save size={16} /> {save.isPending ? "Guardando…" : "Guardar"}</Button>}
        </div>
      </div>
      <div className="grid lg:grid-cols-[minmax(360px,520px)_1fr] gap-4 items-start">
        <Card className="p-0 overflow-hidden">
          <div className="px-3 pt-2"><Tabs<Tab> value={tab} onChange={setTab} tabs={[{ id: "preguntas", label: `Preguntas (${questions.length})` }, { id: "configuracion", label: "Configuración" }]} /></div>
          <fieldset disabled={!admin} className="p-4 max-h-[78vh] overflow-y-auto scrollbar-thin">
            {tab === "preguntas" ? <QuestionsTab questions={questions} onChange={setQs} /> : (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Nombre interno" required><Input value={name} maxLength={160} onChange={(e) => { setName(e.target.value); setDirty(true); }} /></Field>
                  <Field label="Carpeta"><Input value={folder} maxLength={80} onChange={(e) => { setFolder(e.target.value); setDirty(true); }} /></Field>
                </div>
                <Field label="Título visible"><Input value={settings.intro?.title ?? ""} maxLength={200} onChange={(e) => setSt({ ...settings, intro: { ...settings.intro, title: e.target.value } })} /></Field>
                <Field label="Introducción"><Textarea rows={3} maxLength={3000} value={settings.intro?.text ?? ""} onChange={(e) => setSt({ ...settings, intro: { ...settings.intro, text: e.target.value } })} /></Field>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Texto del botón"><Input value={settings.buttonLabel ?? ""} maxLength={60} placeholder="Enviar respuestas" onChange={(e) => setSt({ ...settings, buttonLabel: e.target.value })} /></Field>
                  <Field label="Color principal">
                    <div className="flex gap-2"><input type="color" className="h-9 w-10 rounded border border-line p-0.5 bg-transparent" value={/^#[0-9a-fA-F]{6}$/.test(settings.color ?? "") ? settings.color : "#21386d"} onChange={(e) => setSt({ ...settings, color: e.target.value })} />
                      <Input value={settings.color ?? "#21386d"} maxLength={9} className="font-mono text-xs" onChange={(e) => setSt({ ...settings, color: e.target.value.trim() })} /></div>
                  </Field>
                </div>
                <Field label="Mensaje final"><Textarea rows={2} maxLength={1000} value={settings.thankYou ?? ""} placeholder="¡Gracias por responder la encuesta!" onChange={(e) => setSt({ ...settings, thankYou: e.target.value })} /></Field>
                <Field label="O redirigir a una URL"><Input value={settings.redirectUrl ?? ""} maxLength={500} placeholder="https://…" onChange={(e) => setSt({ ...settings, redirectUrl: e.target.value.trim() })} /></Field>
                <div className="border-t border-line pt-4 space-y-3">
                  <h4 className="sec-title text-sm">Al responder (si hay correo)</h4>
                  <p className="text-xs text-ink-muted">Se crea o actualiza el contacto (dedup por correo) con las preguntas mapeadas. Sin correo, la respuesta se guarda como anónima.</p>
                  <Field label="Etiquetas"><ChipsInput values={settings.tags ?? []} onChange={(tags) => setSt({ ...settings, tags })} placeholder="encuesta-2026" /></Field>
                  <Field label="Añadir a listas"><CheckList items={opts.data?.lists ?? []} selected={settings.listIds ?? []} onChange={(listIds) => setSt({ ...settings, listIds })} empty="Aún no hay listas." /></Field>
                  <Toggle checked={settings.createAccount !== false} onChange={(v) => setSt({ ...settings, createAccount: v })} label="Crear empresa si se mapea «Empresa»" />
                </div>
                <div className="border-t border-line pt-4 space-y-3">
                  <Toggle checked={!!settings.consent?.enabled} onChange={(v) => setSt({ ...settings, consent: { ...settings.consent, enabled: v } })} label="Casilla de consentimiento obligatoria" />
                  {settings.consent?.enabled && (
                    <>
                      <Field label="Texto"><Textarea rows={2} maxLength={600} value={settings.consent.text ?? DEFAULT_CONSENT_TEXT} onChange={(e) => setSt({ ...settings, consent: { ...settings.consent!, text: e.target.value } })} /></Field>
                      <Field label="Política de privacidad"><Input value={settings.consent.policyUrl ?? DEFAULT_POLICY_URL} maxLength={500} onChange={(e) => setSt({ ...settings, consent: { ...settings.consent!, policyUrl: e.target.value.trim() } })} /></Field>
                    </>
                  )}
                </div>
              </div>
            )}
          </fieldset>
        </Card>
        <div className="space-y-2 lg:sticky lg:top-4">
          <div className="flex justify-between items-center">
            <span className="dt-label">Vista previa (prueba la lógica)</span>
            <div className="flex gap-1">
              <button type="button" className={`btn-sm ${!mobile ? "btn-secondary" : "btn-ghost"}`} onClick={() => setMobile(false)}>Escritorio</button>
              <button type="button" className={`btn-sm ${mobile ? "btn-secondary" : "btn-ghost"}`} onClick={() => setMobile(true)}>Móvil</button>
            </div>
          </div>
          <SurveyPreview questions={questions} settings={settings} mobile={mobile} />
        </div>
      </div>
    </div>
  );
}

/** Quita propiedades que no aplican al tipo (el API es estricto). */
function cleanQuestion(q: Question): Question {
  const out: Question = { id: q.id, type: q.type, label: q.label.trim() || "Pregunta", required: !!q.required };
  if (q.description) out.description = q.description;
  if (q.placeholder) out.placeholder = q.placeholder;
  if (hasOptions(q.type)) out.options = (q.options ?? []).map((o) => o.trim()).filter(Boolean);
  if (q.map) out.map = q.map;
  if (q.showIf?.q) out.showIf = q.showIf;
  return out;
}

const ADD_TYPES: QuestionType[] = ["short", "long", "single", "multiple", "dropdown", "scale5", "scale10", "nps", "yesno", "email", "phone", "name"];

function QuestionsTab({ questions, onChange }: { questions: Question[]; onChange: (q: Question[]) => void }): JSX.Element {
  const [newType, setNewType] = useState<QuestionType>("short");
  const patch = (i: number, p: Partial<Question>): void => onChange(questions.map((q, j) => (j === i ? { ...q, ...p } : q)));
  const move = (i: number, d: number): void => {
    const j = i + d; if (j < 0 || j >= questions.length) return;
    const qs = [...questions]; [qs[i], qs[j]] = [qs[j]!, qs[i]!];
    // Una condición solo puede depender de preguntas anteriores: se limpian las que queden inválidas.
    const seen = new Set<string>();
    onChange(qs.map((q) => { const ok = !q.showIf || seen.has(q.showIf.q); seen.add(q.id); return ok ? q : { ...q, showIf: null }; }));
  };
  const remove = (i: number): void => {
    const gone = questions[i]!.id;
    onChange(questions.filter((_, j) => j !== i).map((q) => (q.showIf?.q === gone ? { ...q, showIf: null } : q)));
  };
  const add = (): void => {
    const q: Question = { id: newId(), type: newType, label: QTYPE_LABEL[newType], required: false };
    if (hasOptions(newType)) q.options = ["Opción 1", "Opción 2"];
    onChange([...questions, q]);
  };
  return (
    <div className="space-y-3">
      {!questions.length && <p className="text-sm text-ink-muted">Sin preguntas todavía.</p>}
      {questions.map((q, i) => {
        const prev = questions.slice(0, i);
        const condQ = prev.find((p) => p.id === q.showIf?.q);
        const vals = condQ ? conditionValues(condQ) : [];
        return (
          <div key={q.id} className="border border-line rounded-md p-3 space-y-2.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-ink-muted w-6">{i + 1}.</span>
              <Select className="w-auto text-xs" value={q.type} onChange={(e) => { const t = e.target.value as QuestionType; patch(i, { type: t, ...(hasOptions(t) && !q.options?.length ? { options: ["Opción 1", "Opción 2"] } : {}) }); }}>
                {ADD_TYPES.map((t) => <option key={t} value={t}>{QTYPE_LABEL[t]}</option>)}
              </Select>
              <div className="flex-1" />
              <button type="button" className="btn-ghost btn-sm px-1" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Subir"><ArrowUp size={13} /></button>
              <button type="button" className="btn-ghost btn-sm px-1" onClick={() => move(i, 1)} disabled={i === questions.length - 1} aria-label="Bajar"><ArrowDown size={13} /></button>
              <button type="button" className="btn-ghost btn-sm px-1" onClick={() => onChange([...questions.slice(0, i + 1), { ...q, id: newId(), showIf: q.showIf ?? null }, ...questions.slice(i + 1)])} aria-label="Duplicar"><Copy size={13} /></button>
              <button type="button" className="btn-ghost btn-sm px-1 text-danger" onClick={() => remove(i)} aria-label="Eliminar"><Trash2 size={13} /></button>
            </div>
            <Input value={q.label} maxLength={300} placeholder="Enunciado de la pregunta" onChange={(e) => patch(i, { label: e.target.value })} />
            <Input value={q.description ?? ""} maxLength={1000} placeholder="Descripción (opcional)" className="text-xs" onChange={(e) => patch(i, { description: e.target.value })} />
            {hasOptions(q.type) && (
              <Field label="Opciones (una por línea)">
                <Textarea rows={Math.min(8, Math.max(3, (q.options ?? []).length + 1))} value={(q.options ?? []).join("\n")}
                  onChange={(e) => patch(i, { options: e.target.value.split("\n").map((o) => o.slice(0, 200)).slice(0, 50) })} />
              </Field>
            )}
            {(q.type === "short" || q.type === "long" || q.type === "dropdown") && (
              <Input value={q.placeholder ?? ""} maxLength={120} placeholder="Texto de ayuda (placeholder)" className="text-xs" onChange={(e) => patch(i, { placeholder: e.target.value })} />
            )}
            <div className="flex flex-wrap gap-4 items-center">
              <Toggle checked={!!q.required} onChange={(v) => patch(i, { required: v })} label="Obligatoria" />
              {(q.type === "short" || q.type === "name" || q.type === "email" || q.type === "phone") && (
                <label className="flex items-center gap-2 text-xs text-ink-secondary">Guardar en contacto:
                  <Select className="w-auto text-xs" value={q.map ?? ""} onChange={(e) => patch(i, { map: (e.target.value || null) as ContactMap | null })}>
                    <option value="">{q.type === "email" ? "Correo (auto)" : q.type === "name" ? "Nombre completo (auto)" : q.type === "phone" ? "Teléfono (auto)" : "No"}</option>
                    {(Object.keys(MAP_LABEL) as ContactMap[]).map((m) => <option key={m} value={m}>{MAP_LABEL[m]}</option>)}
                  </Select>
                </label>
              )}
            </div>
            {i > 0 && (
              <div className="rounded-md bg-canvas p-2 space-y-2">
                <label className="flex items-center gap-2 text-xs text-ink-secondary">
                  <GitBranch size={13} />
                  <input type="checkbox" checked={!!q.showIf} onChange={(e) => patch(i, { showIf: e.target.checked ? { q: prev[prev.length - 1]!.id, op: "eq", value: conditionValues(prev[prev.length - 1]!)[0]?.value ?? "" } : null })} />
                  Mostrar solo si…
                </label>
                {q.showIf && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Select className="text-xs" value={q.showIf.q} onChange={(e) => { const p = prev.find((x) => x.id === e.target.value)!; patch(i, { showIf: { q: p.id, op: "eq", value: conditionValues(p)[0]?.value ?? "" } }); }}>
                      {prev.map((p, k) => <option key={p.id} value={p.id}>{k + 1}. {p.label.slice(0, 40)}</option>)}
                    </Select>
                    <Select className="text-xs" value={q.showIf.op} onChange={(e) => patch(i, { showIf: { ...q.showIf!, op: e.target.value as "eq" | "neq" | "includes" | "answered" } })}>
                      <option value="eq">{condQ?.type === "multiple" ? "incluye" : "es igual a"}</option><option value="neq">{condQ?.type === "multiple" ? "no incluye" : "es distinto de"}</option><option value="answered">tiene respuesta</option>
                    </Select>
                    {q.showIf.op !== "answered" && (vals.length
                      ? <Select className="text-xs" value={q.showIf.value ?? ""} onChange={(e) => patch(i, { showIf: { ...q.showIf!, value: e.target.value } })}>{vals.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}</Select>
                      : <Input className="text-xs" value={q.showIf.value ?? ""} maxLength={200} placeholder="Valor exacto" onChange={(e) => patch(i, { showIf: { ...q.showIf!, value: e.target.value } })} />)}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div className="flex gap-2">
        <Select className="w-auto" value={newType} onChange={(e) => setNewType(e.target.value as QuestionType)}>{ADD_TYPES.map((t) => <option key={t} value={t}>{QTYPE_LABEL[t]}</option>)}</Select>
        <Button onClick={add} disabled={questions.length >= 60}><Plus size={16} /> Añadir pregunta</Button>
      </div>
    </div>
  );
}
